import { Button } from "react-bootstrap";
import { useTheme } from "../../../../hooks/useTheme";
import { Entity } from "../../../../models/viz/Entity";
import { useLazyGetAccessCookiesQuery } from "../../../../store/api/kinesense";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import useAsyncCallback from "../../../../hooks/useAsyncCallback";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../store";
import { EntityImageDisplay } from "./EntityImageDisplay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStepBackward, faStepForward } from "@fortawesome/free-solid-svg-icons";
import { faArrowLeftToLine, faArrowRightToLine } from "@fortawesome/pro-solid-svg-icons";

export interface EntityVideoDisplayProps {
    entity: Entity;
}

export function EntityVideoDisplay(props: EntityVideoDisplayProps) {
    const [requestAccessCookies] = useLazyGetAccessCookiesQuery();
    const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined);
    const organisationId = useSelector((state: ApplicationState) => state.user?.organisationId);
    const videoRef: MutableRefObject<HTMLVideoElement> = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [frameIndex, setFrameIndex] = useState(0);
    const [displayFrameTime, setDisplayFrameTime] = useState(new Date(0));
    const [baseTime, setBaseTime] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    const { isDarkTheme } = useTheme();
    const iconPrefix = "/images/icons/" + (isDarkTheme ? "dark-" : "");

    useAsyncCallback(async () => {
        const cookies = await requestAccessCookies({
            mediaId: props.entity.sourceObject.mediaSource?.mediaId,
            target: "group-clip",
        }).unwrap();

        if (cookies === undefined) {
            throw new Error("No valid URL available for the video file");
        }

        const mediaId = props.entity.sourceObject.mediaSource?.mediaId;
        const runId = props.entity.sourceObject.runId;
        const fragGroupId = props.entity.sourceObject.id;
        const fileDirectory = `${organisationId}/${mediaId}/${runId}/${fragGroupId}`;
        const filename = `${fragGroupId}_std.mp4`;
        const domain = `https://${process.env.REACT_APP_KINESENSE_IMAGES_BASE}`;
        const cookieQuery = `Policy=${cookies["CloudFront-Policy"]}&Key-Pair-Id=${cookies["CloudFront-Key-Pair-Id"]}&Signature=${cookies["CloudFront-Signature"]}`;

        setVideoSrc(`${domain}/${fileDirectory}/${filename}?${cookieQuery}`);
    }, [props.entity]);

    useEffect(() => {
        if (videoRef.current && videoSrc) {
            videoRef.current.src = videoSrc;
            videoRef.current.addEventListener("play", handleOnVideoPlay);
            videoRef.current.addEventListener("pause", handleOnVideoPause);
            videoRef.current.addEventListener("ended", handleOnVideoPause);
            videoRef.current.addEventListener("seeked", handleOnVideoPlayerTimeUpdate);
            videoRef.current.addEventListener("timeupdate", handleOnVideoPlayerTimeUpdate);
        }

        return () => {
            if (videoRef.current) {
                videoRef.current.removeEventListener("play", handleOnVideoPlay);
                videoRef.current.removeEventListener("pause", handleOnVideoPause);
                videoRef.current.removeEventListener("ended", handleOnVideoPause);
                videoRef.current.removeEventListener("seeked", handleOnVideoPlayerTimeUpdate);
                videoRef.current.removeEventListener("timeupdate", handleOnVideoPlayerTimeUpdate);
            }
        };
    }, [videoSrc]);

    useEffect(() => {
        const defaultDate = new Date(0);
        const startTime = props.entity.getStartTimeOr(defaultDate);

        if (startTime == defaultDate) {
            setBaseTime(0);
        } else {
            setBaseTime(startTime.getTime());
        }
    }, [props.entity]);

    useEffect(() => {
        const time = baseTime + currentTime;
        setDisplayFrameTime(new Date(time));

        if (props.entity.images.videoPlaybackMode == "video") {
            setFrameIndex(
                Math.min(
                    props.entity.images.video.frameCount - 1,
                    Math.round(currentTime / (1000 / props.entity.images.video.frameRate)),
                ),
            );
        }
    }, [baseTime, currentTime]);

    useEffect(() => {
        if (props.entity.images.videoPlaybackMode == "frames") {
            const newTime = frameIndex * (1000 / props.entity.images.video.frameRate);
            setCurrentTime(newTime);
        }
    }, [frameIndex]);

    const handleOnVideoPlayerTimeUpdate = () => {
        if (videoRef.current) {
            const time = videoRef.current.currentTime * 1000;
            setCurrentTime(time);
        }
    };

    useEffect(() => {
        if (props.entity.images.videoPlaybackMode == "frames" && isPlaying) {
            const max = props.entity.images.images.length - 1;
            const newFrameIndex = Math.min(Math.max(0, frameIndex + 1), max);

            if (newFrameIndex <= max) {
                console.log("starting playback, frame index: ", newFrameIndex);
                queueNextFrame(newFrameIndex);
            }

            if (newFrameIndex >= max) {
                console.log("Stopping playback, reached end of video (useEffect)");
                setIsPlaying(false);
            }
        }
    }, [isPlaying]);

    const handlePlayButtonClick = () => {
        if (props.entity.images.videoPlaybackMode == "video") {
            if (videoRef.current) {
                if (videoRef.current.paused) {
                    videoRef.current.play().catch((error) => {
                        console.error("Error playing video:", error);
                    });
                } else {
                    videoRef.current.pause();
                }
            }
        } else {
            setIsPlaying(!isPlaying);
        }
    };

    const queueNextFrame = (index: number) => {
        const framePeriod = (1000 / props.entity.images.video.frameRate) * 4;
        setTimeout(() => {
            const max = props.entity.images.images.length - 1;
            const newFrameIndex = Math.min(Math.max(0, index + 1), max);

            setFrameIndex(index);

            console.log("Frame index, max: ", newFrameIndex, max);
            console.log("Is playing: ", isPlaying);

            if (newFrameIndex <= max && index != newFrameIndex) {
                console.log("Queing next frame");
                queueNextFrame(newFrameIndex);
            }

            if (newFrameIndex >= max) {
                console.log("Stopping playback, reached end of video");
                setIsPlaying(false);
            }
        }, framePeriod);
    };

    const handleJumpToStartButtonClick = () => {
        if (props.entity.images.videoPlaybackMode == "video") {
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        } else {
            setFrameIndex(0);
        }
    };

    const handleJumpToEndButtonClick = () => {
        if (props.entity.images.videoPlaybackMode == "video") {
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = videoRef.current.duration;
            }
        } else {
            setFrameIndex(props.entity.images.images.length - 1);
        }
    };

    const handlePreviousFrameButtonClick = () => {
        if (props.entity.images.videoPlaybackMode == "video") {
            const framePeriod = 1 / props.entity.images.video.frameRate;
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - framePeriod);
            }
        } else {
            const max = props.entity.images.images.length - 1;
            const newFrameIndex = Math.min(Math.max(0, frameIndex - 1), max);

            setFrameIndex(newFrameIndex);
        }
    };

    const handleNextFrameButtonClick = () => {
        if (props.entity.images.videoPlaybackMode == "video") {
            const framePeriod = 1 / props.entity.images.video.frameRate;
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = Math.min(
                    videoRef.current.duration,
                    videoRef.current.currentTime + framePeriod,
                );
            }
        } else {
            const max = props.entity.images.images.length - 1;
            const newFrameIndex = Math.min(Math.max(0, frameIndex + 1), max);

            setFrameIndex(newFrameIndex);
        }
    };

    const handleOnVideoPlay = () => {
        setIsPlaying(true);
    };

    const handleOnVideoPause = () => {
        setIsPlaying(false);
    };

    return (
        <>
            <div className="viz-preview-thumbnail position-relative">
                {props.entity.images.videoPlaybackMode == "video" && videoSrc != undefined && (
                    <div
                        style={{
                            height: "100%",
                            maxWidth: "100%",
                            margin: "auto",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <video style={{ width: "100%", height: "100%" }} ref={videoRef} />
                    </div>
                )}

                {props.entity.images.videoPlaybackMode == "frames" && videoSrc != undefined && (
                    <div style={{ height: "100%", maxWidth: "100%", margin: "auto" }}>
                        <EntityImageDisplay
                            entityImage={props.entity.images.images[frameIndex]}
                            mediaId={props.entity.sourceObject.mediaSource?.mediaId}
                        />
                    </div>
                )}
            </div>

            <div style={{ display: "flex", justifyContent: "center", marginTop: "0.7rem" }}>
                <Button variant="secondary" className="standard-button" onClick={handlePlayButtonClick}>
                    <img src={iconPrefix + (isPlaying ? "pause_56.png" : "play_56.png")} />
                </Button>
            </div>

            <div className="viz-preview-video-controls">
                <Button
                    variant="secondary"
                    className="small-button"
                    disabled={frameIndex === 0}
                    onClick={handleJumpToStartButtonClick}
                >
                    <FontAwesomeIcon icon={faArrowLeftToLine} className="fa-fw" />
                </Button>
                <Button
                    variant="secondary"
                    className="standard-button"
                    disabled={frameIndex === 0}
                    onClick={handlePreviousFrameButtonClick}
                >
                    <FontAwesomeIcon icon={faStepBackward} className="fa-fw" />
                </Button>

                <div className="viz-preview-video-time">
                    {/* {props.entity.images.videoPlaybackMode == "frames" && ( */}
                    <div className="viz-preview-video-time-header">
                        Frame {frameIndex + 1} of {props.entity.images.video.frameCount}
                    </div>
                    {/* )} */}
                    <div className="viz-preview-video-time-body">
                        <span>
                            {String(displayFrameTime.getUTCHours()).padStart(2, "0")}:
                            {String(displayFrameTime.getUTCMinutes()).padStart(2, "0")}:
                            {String(displayFrameTime.getUTCSeconds()).padStart(2, "0")}
                        </span>
                        <span className="time-milliseconds">
                            .{String(displayFrameTime.getUTCMilliseconds()).padStart(3, "0")}
                        </span>
                    </div>
                </div>

                <Button
                    variant="secondary"
                    className="standard-button"
                    disabled={frameIndex >= props.entity.images.video.frameCount - 1}
                    onClick={handleNextFrameButtonClick}
                >
                    <FontAwesomeIcon icon={faStepForward} className="fa-fw" />
                </Button>
                <Button
                    variant="secondary"
                    className="small-button"
                    disabled={frameIndex >= props.entity.images.video.frameCount - 1}
                    onClick={handleJumpToEndButtonClick}
                >
                    <FontAwesomeIcon icon={faArrowRightToLine} className="fa-fw" />
                </Button>
            </div>
        </>
    );
}
